import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { UserProfile } from '../../types/customer';
import DashboardLayout from '../../components/layout/DashboardLayout';
import UserSidebar from '../../components/sidebar/UserSidebar';
import { userApi } from '../../services/userApi';
import './ProfileManagementPage.css';

const ProfileManagementPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [profile, setProfile] = useState<UserProfile>({
    id: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    bvn: '',
    tin: '',
    companyName: '',
    directorInfo: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [is2faEnabled, setIs2faEnabled] = useState(false);
  const [is2faUpdating, setIs2faUpdating] = useState(false);
  const [twoFactorMessage, setTwoFactorMessage] = useState('');
  const [twoFactorSetup, setTwoFactorSetup] = useState<{
    qrCodeDataUrl: string;
    manualEntryKey: string;
  } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isDisabling2fa, setIsDisabling2fa] = useState(false);

  useEffect(() => {
    const initializeProfile = async () => {
      if (currentUser) {
        setProfile({
          id: currentUser.id,
          name: currentUser.name,
          email: currentUser.email,
          phone: '',
          address: '',
          bvn: '',
          tin: '',
          companyName: '',
          directorInfo: []
        });
        setIs2faEnabled(!!currentUser.twoFactorEnabled);
      }

      try {
        const response = await userApi.getProfile();
        setIs2faEnabled(!!response.data.user.twoFactorEnabled);
      } catch (error) {
        console.error('Failed to fetch latest profile settings', error);
      }
    };

    initializeProfile();
  }, [currentUser]);

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDirectorInfoChange = (index: number, field: keyof UserProfile['directorInfo'][number], value: string) => {
    setProfile(prev => ({
      ...prev,
      directorInfo: prev.directorInfo.map((director, i) => 
        i === index ? { ...director, [field]: value } : director
      )
    }));
  };

  const addDirector = () => {
    setProfile(prev => ({
      ...prev,
      directorInfo: [...prev.directorInfo, { name: '', ninOrBvn: '', position: '' }]
    }));
  };

  const removeDirector = (index: number) => {
    setProfile(prev => ({
      ...prev,
      directorInfo: prev.directorInfo.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Profile saved:', profile);
    setIsEditing(false);
    setIsSaving(false);
    
    // Show success message (would typically use a toast notification)
    alert('Profile updated successfully!');
  };

  const handleCancel = () => {
    if (currentUser) {
      setProfile({
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        phone: '',
        address: '',
        bvn: '',
        tin: '',
        companyName: '',
        directorInfo: []
      });
    }
    setIsEditing(false);
  };

  const handleStartTwoFactorSetup = async () => {
    setIs2faUpdating(true);
    setTwoFactorMessage('');
    setTwoFactorSetup(null);
    setTwoFactorCode('');

    try {
      const response = await userApi.startTwoFactorSetup();
      setTwoFactorSetup({
        qrCodeDataUrl: response.data.qrCodeDataUrl,
        manualEntryKey: response.data.manualEntryKey
      });
      setTwoFactorMessage(response.message);
    } catch (error) {
      console.error('Failed to start 2FA setup', error);
      setTwoFactorMessage('Unable to start authenticator setup. Please try again later.');
    } finally {
      setIs2faUpdating(false);
    }
  };

  const handleConfirmTwoFactorSetup = async () => {
    if (!/^\d{6}$/.test(twoFactorCode)) {
      setTwoFactorMessage('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setIs2faUpdating(true);
    setTwoFactorMessage('');

    try {
      const response = await userApi.confirmTwoFactorSetup({ twoFactorCode });
      setIs2faEnabled(!!response.data.user.twoFactorEnabled);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorMessage(response.message);
    } catch (error) {
      console.error('Failed to confirm 2FA setup', error);
      setTwoFactorMessage('Invalid authenticator code. Check the app code and try again.');
    } finally {
      setIs2faUpdating(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    if (!isDisabling2fa) {
      setIsDisabling2fa(true);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorMessage('Enter a current authenticator code to disable 2FA.');
      return;
    }

    if (!/^\d{6}$/.test(twoFactorCode)) {
      setTwoFactorMessage('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setIs2faUpdating(true);
    setTwoFactorMessage('');

    try {
      const response = await userApi.disableTwoFactor({ twoFactorCode });
      setIs2faEnabled(!!response.data.user.twoFactorEnabled);
      setIsDisabling2fa(false);
      setTwoFactorCode('');
      setTwoFactorMessage(response.message);
    } catch (error) {
      console.error('Failed to disable 2FA', error);
      setTwoFactorMessage('Invalid authenticator code. 2FA was not disabled.');
    } finally {
      setIs2faUpdating(false);
    }
  };

  const handleCancelTwoFactorAction = () => {
    setTwoFactorSetup(null);
    setIsDisabling2fa(false);
    setTwoFactorCode('');
    setTwoFactorMessage('');
  };

  return (
    <DashboardLayout sidebar={<UserSidebar />}>
      <div className="profile-management-page">
      <div className="page-header">
        <h2>Profile Management</h2>
        <p>Update your personal and company information</p>
      </div>

      <div className="profile-container">
        <div className="profile-header">
          <h3>Personal Information</h3>
          {!isEditing && (
            <button 
              onClick={() => setIsEditing(true)}
              className="edit-btn"
            >
              Edit Profile
            </button>
          )}
        </div>

        <div id="two-factor" className="two-factor-section mb-6 p-5 rounded-xl border border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-lg font-semibold">Two-Factor Authentication</h4>
              <p className="text-sm text-slate-600">
                Use an authenticator app for an extra login step.
              </p>
            </div>
            {!twoFactorSetup && !isDisabling2fa && (
              <button
                type="button"
                className="btn-secondary"
                onClick={is2faEnabled ? handleDisableTwoFactor : handleStartTwoFactorSetup}
                disabled={is2faUpdating}
              >
                {is2faUpdating ? 'Saving...' : is2faEnabled ? 'Disable 2FA' : 'Set up 2FA'}
              </button>
            )}
          </div>
          <p className="text-sm">
            Status: <span className="font-semibold">{is2faEnabled ? 'Enabled' : 'Disabled'}</span>
          </p>
          {twoFactorSetup && (
            <div className="two-factor-setup">
              <img src={twoFactorSetup.qrCodeDataUrl} alt="Authenticator app QR code" />
              <div className="two-factor-setup-details">
                <p>Scan this QR code in your authenticator app, then enter the 6-digit code.</p>
                <div className="manual-key">
                  <span>Manual key</span>
                  <code>{twoFactorSetup.manualEntryKey}</code>
                </div>
                <div className="two-factor-code-row">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    aria-label="Authenticator code"
                  />
                  <button type="button" className="save-btn" onClick={handleConfirmTwoFactorSetup} disabled={is2faUpdating}>
                    Confirm
                  </button>
                  <button type="button" className="cancel-btn" onClick={handleCancelTwoFactorAction} disabled={is2faUpdating}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {isDisabling2fa && (
            <div className="two-factor-disable">
              <input
                type="text"
                inputMode="numeric"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Authenticator code"
                maxLength={6}
                aria-label="Authenticator code"
              />
              <button type="button" className="remove-director-btn" onClick={handleDisableTwoFactor} disabled={is2faUpdating}>
                Disable
              </button>
              <button type="button" className="cancel-btn" onClick={handleCancelTwoFactorAction} disabled={is2faUpdating}>
                Cancel
              </button>
            </div>
          )}
          {twoFactorMessage && (
            <p className="mt-3 text-sm text-slate-700">{twoFactorMessage}</p>
          )}
        </div>

        <div className="profile-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                value={profile.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={profile.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                value={profile.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={!isEditing}
                placeholder="+234 708 832 9998"
              />
            </div>
            <div className="form-group">
              <label htmlFor="address">Address</label>
              <input
                type="text"
                id="address"
                value={profile.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                disabled={!isEditing}
                placeholder="Your complete address"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bvn">BVN (Bank Verification Number)</label>
              <input
                type="text"
                id="bvn"
                value={profile.bvn}
                onChange={(e) => handleInputChange('bvn', e.target.value)}
                disabled={!isEditing}
                placeholder="11-digit BVN"
                maxLength={11}
              />
            </div>
            <div className="form-group">
              <label htmlFor="tin">TIN (Tax Identification Number)</label>
              <input
                type="text"
                id="tin"
                value={profile.tin}
                onChange={(e) => handleInputChange('tin', e.target.value)}
                disabled={!isEditing}
                placeholder="TIN number"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              value={profile.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              disabled={!isEditing}
              placeholder="Your company name"
            />
          </div>

          <div className="directors-section">
            <h4>Director Information</h4>
            {profile.directorInfo.map((director, index) => (
              <div key={index} className="director-card">
                <div className="form-row">
                  <div className="form-group">
                    <label>Director Name</label>
                    <input
                      type="text"
                      value={director.name}
                      onChange={(e) => handleDirectorInfoChange(index, 'name', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="form-group">
                    <label>NIN/BVN</label>
                    <input
                      type="text"
                      value={director.ninOrBvn}
                      onChange={(e) => handleDirectorInfoChange(index, 'ninOrBvn', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Identification number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Position</label>
                    <input
                      type="text"
                      value={director.position}
                      onChange={(e) => handleDirectorInfoChange(index, 'position', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Director position"
                    />
                  </div>
                </div>
                {isEditing && (
                  <button
                    onClick={() => removeDirector(index)}
                    className="remove-director-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            {isEditing && (
              <button onClick={addDirector} className="add-director-btn">
                + Add Director
              </button>
            )}
          </div>

          {isEditing && (
            <div className="form-actions">
              <button
                onClick={handleCancel}
                className="cancel-btn"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="save-btn"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </DashboardLayout>
  );
};

export default ProfileManagementPage;
